import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useCriarPedidoRenovacao, type EquipamentoRenovacaoInput } from "@/hooks/usePedidos";
import {
  buscarPedidoOrigemRenovacao,
  resolverPedidoIdPorEvento,
  type PedidoOrigemRenovacao,
} from "@/lib/resolverPedidoRenovacao";
import { formatNumeroCicloVenda, proximoNumeroCiclo } from "@/lib/pedidosCiclo";
import { usePlanosAtivos } from "@/hooks/usePlanos";
import { useEquipamentos } from "@/hooks/useEquipamentos";
import { supabase } from "@/integrations/supabase/client";
import type { EventoAgenda } from "@/hooks/useAgenda";

type EquipLinha = EquipamentoRenovacaoInput & {
  marca?: string;
  modelo?: string;
  tipo?: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pedidoAnteriorId?: number | null;
  evento?: EventoAgenda | null;
  concluirEventoAgenda?: boolean;
  permitirArquivoNaoRenovado?: boolean;
  onSuccess?: (orcamentoId: number) => void;
};

const STEPS = ["Plano", "Equipamentos", "Desconto", "Resumo"] as const;

function formatBRL(valor: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(valor || 0);
}

export function CriarPedidoRenovacaoDialog({
  open,
  onOpenChange,
  pedidoAnteriorId: pedidoAnteriorIdProp,
  evento,
  permitirArquivoNaoRenovado = false,
  onSuccess,
}: Props) {
  const criarRenovacao = useCriarPedidoRenovacao();
  const { data: planosAtivos } = usePlanosAtivos();
  const { data: equipamentosCatalogo } = useEquipamentos();
  const [step, setStep] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [origem, setOrigem] = useState<PedidoOrigemRenovacao | null>(null);
  const [pedidoAnteriorId, setPedidoAnteriorId] = useState<number | null>(null);
  const [erroCarregamento, setErroCarregamento] = useState<string | null>(null);

  const [manterPlano, setManterPlano] = useState(true);
  const [planoId, setPlanoId] = useState<string>("");
  const [qtdPaineis, setQtdPaineis] = useState(0);
  const [vigenciaAnos, setVigenciaAnos] = useState(1);
  const [equipamentos, setEquipamentos] = useState<EquipLinha[]>([]);
  const [eqTipo, setEqTipo] = useState("painel");
  const [eqId, setEqId] = useState("");
  const [eqQtd, setEqQtd] = useState(1);
  const [valorFinal, setValorFinal] = useState("");
  const [observacoes, setObservacoes] = useState("");

  useEffect(() => {
    if (!open) {
      setOrigem(null);
      setPedidoAnteriorId(null);
      setErroCarregamento(null);
      setObservacoes("");
      setStep(0);
      setEquipamentos([]);
      setValorFinal("");
      setManterPlano(true);
      return;
    }

    let cancelled = false;

    (async () => {
      setCarregando(true);
      setErroCarregamento(null);
      try {
        let id = pedidoAnteriorIdProp ?? null;
        if (!id && evento) {
          id = await resolverPedidoIdPorEvento(evento);
        }
        if (!id) {
          setErroCarregamento(
            "Não foi possível identificar o pedido de origem. Abra o pedido na listagem e use a ação de renovação."
          );
          return;
        }
        if (cancelled) return;

        const pedido = await buscarPedidoOrigemRenovacao(id);
        if (cancelled) return;

        if (!pedido) {
          setErroCarregamento("Pedido de origem não encontrado.");
          return;
        }

        setPedidoAnteriorId(id);
        setOrigem(pedido);
        const planoOrigem = String(pedido.orcamento?.plano_id || "");
        setPlanoId(planoOrigem);
        setManterPlano(true);
        setQtdPaineis(Number(pedido.orcamento?.quantidade_placas) || 0);
        setVigenciaAnos(Number(pedido.orcamento?.vigencia_anos) || 1);

        let eqs: EquipLinha[] = [];
        const orcId = pedido.orcamento?.id ?? pedido.orcamento_id;
        if (orcId) {
          const { data: eqsOrc } = await supabase
            .from("orcamento_equipamentos")
            .select("equipamento_id, quantidade, preco_unitario, equipamento:equipamentos(marca, modelo, tipo)")
            .eq("orcamento_id", orcId);
          eqs = (eqsOrc || []).map((e: any) => ({
            equipamento_id: e.equipamento_id,
            quantidade: e.quantidade,
            preco_unitario: e.preco_unitario,
            marca: e.equipamento?.marca,
            modelo: e.equipamento?.modelo,
            tipo: e.equipamento?.tipo,
          }));
        }
        if (eqs.length === 0 && pedido.projeto_id) {
          const { data: eqsProj } = await supabase
            .from("projeto_equipamentos")
            .select("equipamento_id, quantidade, preco_unitario, equipamento:equipamentos(marca, modelo, tipo)")
            .eq("projeto_id", pedido.projeto_id);
          eqs = (eqsProj || []).map((e: any) => ({
            equipamento_id: e.equipamento_id,
            quantidade: e.quantidade,
            preco_unitario: e.preco_unitario,
            marca: e.equipamento?.marca,
            modelo: e.equipamento?.modelo,
            tipo: e.equipamento?.tipo,
          }));
        }
        if (!cancelled) setEquipamentos(eqs);
      } catch (e: unknown) {
        if (!cancelled) {
          setErroCarregamento((e as Error)?.message || "Erro ao carregar pedido de origem.");
        }
      } finally {
        if (!cancelled) setCarregando(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, pedidoAnteriorIdProp, evento]);

  const planoEfetivoId = manterPlano ? String(origem?.orcamento?.plano_id || planoId) : planoId;
  const planoSelecionado = planosAtivos?.find((p) => String(p.id) === String(planoEfetivoId));

  const valorTabela = useMemo(() => {
    const valorPorPainel = Number(planoSelecionado?.valor_por_painel) || 0;
    const base = (qtdPaineis || 0) * valorPorPainel;
    const calculado = vigenciaAnos === 2 ? base * 1.8 : base;
    if (calculado > 0) return calculado;
    return Number(origem?.orcamento?.valor_seguro || origem?.orcamento?.valor_total || origem?.valor_total || 0);
  }, [planoSelecionado, qtdPaineis, vigenciaAnos, origem]);

  useEffect(() => {
    if (open && valorTabela > 0 && !valorFinal) {
      setValorFinal(valorTabela.toFixed(2).replace(".", ","));
    }
  }, [open, valorTabela, valorFinal]);

  const valorFinalNum = useMemo(() => {
    const n = Number(String(valorFinal).replace(/\./g, "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  }, [valorFinal]);

  const descontoValor = Math.max(0, Math.round((valorTabela - valorFinalNum) * 100) / 100);
  const numeroCicloNovo = origem
    ? proximoNumeroCiclo(origem.numero_ciclo, origem.tipo_negocio)
    : 2;

  const eqsFiltrados = (equipamentosCatalogo || []).filter((e) => e.tipo === eqTipo);

  const handleAddEquip = () => {
    const eq = equipamentosCatalogo?.find((e) => String(e.id) === String(eqId));
    if (!eq) {
      toast.error("Selecione um equipamento");
      return;
    }
    if (eqQtd <= 0) {
      toast.error("Quantidade deve ser maior que zero");
      return;
    }
    setEquipamentos((prev) => [
      ...prev,
      {
        equipamento_id: Number(eq.id),
        quantidade: eqQtd,
        preco_unitario: eq.preco_unitario ?? 0,
        marca: eq.marca,
        modelo: eq.modelo,
        tipo: eq.tipo,
      },
    ]);
    setEqId("");
    setEqQtd(1);
  };

  const handleConfirmar = async () => {
    if (!pedidoAnteriorId) return;
    if (!planoEfetivoId) {
      toast.error("Selecione um plano");
      return;
    }
    if (!qtdPaineis || qtdPaineis <= 0) {
      toast.error("Informe a quantidade de painéis");
      return;
    }
    if (!valorFinalNum || valorFinalNum <= 0) {
      toast.error("Informe o valor final da proposta");
      return;
    }

    try {
      const result = await criarRenovacao.mutateAsync({
        pedidoAnteriorId,
        planoId: planoEfetivoId,
        quantidadePlacas: qtdPaineis,
        vigenciaAnos,
        valorTabela,
        valorFinal: valorFinalNum,
        observacoesExtras: observacoes,
        equipamentos: equipamentos.map((e) => ({
          equipamento_id: e.equipamento_id,
          quantidade: e.quantidade,
          preco_unitario: e.preco_unitario,
        })),
        formaPagamento: origem?.orcamento?.forma_pagamento,
        parcelasCartao: origem?.orcamento?.parcelas_cartao,
        numeroParcelas: origem?.orcamento?.numero_parcelas,
        permitirArquivoNaoRenovado,
      });

      toast.success(
        `Proposta de ${formatNumeroCicloVenda(result.numeroCiclo)} criada. Orçamento #${result.orcamentoNovo.id} pendente de aprovação.`
      );
      onOpenChange(false);
      onSuccess?.(result.orcamentoNovo.id);
      window.dispatchEvent(new CustomEvent("mudar_secao", { detail: "orcamentos" }));
    } catch (e: unknown) {
      toast.error((e as Error)?.message || "Erro ao criar proposta de renovação");
    }
  };

  const canNext = () => {
    if (step === 0) return Boolean(planoEfetivoId) && qtdPaineis > 0;
    if (step === 2) return valorFinalNum > 0 && valorTabela > 0;
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-600" />
            Proposta de renovação
            {origem && (
              <span className="text-sm font-normal text-gray-500">
                — {formatNumeroCicloVenda(numeroCicloNovo)}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {carregando && (
          <div className="flex items-center justify-center py-8 text-gray-600">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            Carregando dados do ciclo anterior...
          </div>
        )}

        {!carregando && erroCarregamento && (
          <p className="text-sm text-red-600 py-4">{erroCarregamento}</p>
        )}

        {!carregando && origem && pedidoAnteriorId && (
          <div className="space-y-4">
            <div className="flex gap-1 text-xs">
              {STEPS.map((label, i) => (
                <button
                  key={label}
                  type="button"
                  className={`flex-1 rounded px-2 py-1 ${
                    i === step
                      ? "bg-blue-600 text-white"
                      : i < step
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-500"
                  }`}
                  onClick={() => i <= step && setStep(i)}
                >
                  {i + 1}. {label}
                </button>
              ))}
            </div>

            <div className="rounded-lg bg-gray-50 p-3 text-sm">
              <p>
                Pedido origem: <strong>#{String(pedidoAnteriorId).padStart(6, "0")}</strong>
                {" · "}
                {origem.cliente?.nome || `ID ${origem.cliente_id}`}
              </p>
            </div>

            {step === 0 && (
              <div className="space-y-4">
                <div>
                  <Label>Plano</Label>
                  <RadioGroup
                    className="mt-2"
                    value={manterPlano ? "mesmo" : "outro"}
                    onValueChange={(v) => {
                      const mesmo = v === "mesmo";
                      setManterPlano(mesmo);
                      if (mesmo) setPlanoId(String(origem.orcamento?.plano_id || ""));
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="mesmo" id="plano-mesmo" />
                      <Label htmlFor="plano-mesmo" className="cursor-pointer">
                        Manter o plano atual
                        {planosAtivos?.find((p) => String(p.id) === String(origem.orcamento?.plano_id))?.nome
                          ? ` (${planosAtivos.find((p) => String(p.id) === String(origem.orcamento?.plano_id))?.nome})`
                          : ""}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="outro" id="plano-outro" />
                      <Label htmlFor="plano-outro" className="cursor-pointer">
                        Escolher outro plano
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {!manterPlano && (
                  <RadioGroup value={planoId} onValueChange={setPlanoId} className="space-y-2">
                    {planosAtivos?.map((plano) => (
                      <div key={plano.id} className="flex items-start space-x-2">
                        <RadioGroupItem value={String(plano.id)} id={`plano-${plano.id}`} className="mt-3" />
                        <Label htmlFor={`plano-${plano.id}`} className="flex-1 cursor-pointer">
                          <div className="p-3 border rounded-lg hover:bg-gray-50">
                            <h4 className="font-medium">{plano.nome}</h4>
                            <p className="text-sm text-gray-600">{plano.descricao}</p>
                            <p className="text-sm font-medium text-green-600 mt-1">
                              {formatBRL(Number(plano.valor_por_painel) || 0)} por painel
                            </p>
                          </div>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="qtd-paineis">Quantidade de painéis</Label>
                    <Input
                      id="qtd-paineis"
                      type="number"
                      min={1}
                      className="mt-1"
                      value={qtdPaineis || ""}
                      onChange={(e) => setQtdPaineis(Number(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Vigência</Label>
                    <Select value={String(vigenciaAnos)} onValueChange={(v) => setVigenciaAnos(Number(v))}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 ano</SelectItem>
                        <SelectItem value="2">2 anos (10% de desconto)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {planoSelecionado && (
                  <p className="text-sm text-gray-600">
                    Valor de tabela: <strong>{formatBRL(valorTabela)}</strong>
                  </p>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Equipamentos do ciclo anterior. Inclua os que o projeto ganhou.
                </p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left p-2">Equipamento</th>
                        <th className="text-center p-2">Qtd</th>
                        <th className="p-2 w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {equipamentos.map((eq, idx) => (
                        <tr key={`${eq.equipamento_id}-${idx}`} className="border-b">
                          <td className="p-2">
                            {[eq.tipo, eq.marca, eq.modelo].filter(Boolean).join(" · ") ||
                              `ID ${eq.equipamento_id}`}
                          </td>
                          <td className="p-2 text-center">{eq.quantidade}</td>
                          <td className="p-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600"
                              onClick={() => setEquipamentos((prev) => prev.filter((_, i) => i !== idx))}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {equipamentos.length === 0 && (
                        <tr>
                          <td colSpan={3} className="p-3 text-center text-gray-500">
                            Nenhum equipamento. Adicione abaixo.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="grid grid-cols-[1fr_1fr_80px_auto] gap-2 items-end">
                  <div>
                    <Label>Tipo</Label>
                    <Select value={eqTipo} onValueChange={(v) => { setEqTipo(v); setEqId(""); }}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="painel">Painel</SelectItem>
                        <SelectItem value="inversor">Inversor</SelectItem>
                        <SelectItem value="estrutura">Estrutura</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Equipamento</Label>
                    <Select value={eqId} onValueChange={setEqId}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Selecionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {eqsFiltrados.map((e) => (
                          <SelectItem key={e.id} value={String(e.id)}>
                            {e.marca} {e.modelo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Qtd</Label>
                    <Input
                      type="number"
                      min={1}
                      className="mt-1"
                      value={eqQtd}
                      onChange={(e) => setEqQtd(Number(e.target.value) || 1)}
                    />
                  </div>
                  <Button type="button" variant="outline" onClick={handleAddEquip}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3">
                <div className="rounded-lg border p-4 space-y-2 text-sm">
                  <p>
                    Valor de tabela: <strong>{formatBRL(valorTabela)}</strong>
                  </p>
                  <p className="text-xs text-gray-500">
                    {qtdPaineis} painéis × {formatBRL(Number(planoSelecionado?.valor_por_painel) || 0)}
                    {vigenciaAnos === 2 ? " × 1,8 (2 anos)" : ""}
                  </p>
                </div>
                <div>
                  <Label htmlFor="valor-final">Valor final da proposta (R$)</Label>
                  <Input
                    id="valor-final"
                    className="mt-1"
                    value={valorFinal}
                    onChange={(e) => setValorFinal(e.target.value)}
                    placeholder="179,00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Desconto comercial: <strong>{formatBRL(descontoValor)}</strong>
                  </p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-3">
                <div className="rounded-lg bg-gray-50 p-4 text-sm space-y-2">
                  <p>
                    Ciclo: <strong>{formatNumeroCicloVenda(numeroCicloNovo)}</strong>
                  </p>
                  <p>
                    Plano: <strong>{planoSelecionado?.nome || planoEfetivoId || "—"}</strong>
                  </p>
                  <p>
                    Painéis: <strong>{qtdPaineis}</strong> · Equipamentos:{" "}
                    <strong>{equipamentos.length}</strong>
                  </p>
                  <p>
                    Tabela: <strong>{formatBRL(valorTabela)}</strong> → Final:{" "}
                    <strong>{formatBRL(valorFinalNum)}</strong>
                    {descontoValor > 0 && (
                      <span className="text-gray-500"> (desconto {formatBRL(descontoValor)})</span>
                    )}
                  </p>
                  {descontoValor > 0 && (
                    <p className="text-xs text-blue-700">
                      O desconto comercial será registrado no seu nome.
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    Será gerado um orçamento pendente. A aprovação cria o pedido desta venda; o contrato
                    da 1ª venda será atualizado depois, no fluxo normal.
                  </p>
                </div>
                <div>
                  <Label>Observações (opcional)</Label>
                  <Textarea
                    value={observacoes}
                    onChange={(e) => setObservacoes(e.target.value)}
                    placeholder="Ex.: desconto comercial combinado com o cliente"
                    rows={2}
                    className="mt-1"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-between gap-2 pt-2">
              <Button variant="outline" onClick={() => (step === 0 ? onOpenChange(false) : setStep((s) => s - 1))}>
                {step === 0 ? "Cancelar" : "Voltar"}
              </Button>
              {step < STEPS.length - 1 ? (
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={!canNext()}
                  onClick={() => {
                    if (step === 2 && valorTabela > 0 && !valorFinal) {
                      setValorFinal(valorTabela.toFixed(2).replace(".", ","));
                    }
                    setStep((s) => s + 1);
                  }}
                >
                  Continuar
                </Button>
              ) : (
                <Button
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleConfirmar}
                  disabled={criarRenovacao.isPending || !canNext()}
                >
                  {criarRenovacao.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Gerando proposta...
                    </>
                  ) : (
                    "Gerar orçamento pendente"
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
